import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    FrequencySlicerOptions,
    ICategoryAdvFilters,
    ICerbosData,
    ICheckedNode,
    IDRange,
    IDateLogs,
    IDprCheckedData,
    IFixedReviewOptions,
    IMainContext,
    IReviewsSkuData,
    ISkusData,
    IStrapiAssetResponse,
    IStrapiResponse,
    ParentModule,
    Rule,
    SelectOptions,
    SubModule,
    TCommonRowData,
    TDayPartingDisplay,
    TDprSlots,
    TIsDaypartingEdit,
} from "@ts/common-types";
import { DEFAULT_REVIEW_OPTIONS, DPR_TIME_SLOTS, mainContext } from "@ts/constants";
import { updateClientTimezone } from "@features/adminSettingsSlice";
import { getActiveModule, setQueryParams } from "@ts/utils";
import { GridApi, ICellRendererParams, RowNode } from "ag-grid-community";

interface Props {
    children: React.ReactNode;
}

const ContextWrapper: React.FC<Props> = (props) => {
    const dispatch = useDispatch();
    const [dates, setDates] = useState<number>(1);
    const [rulesApi, setRulesApi] = useState<GridApi>();
    const [alertsData, setAlertsData] = useState<Alert[]>();
    const [activeRulesData, setActiveRulesData] = useState<
        Rule[] | TCommonRowData
    >();
    const [portfoliosGridApi, setPortfoliosGridApi] = useState<GridApi>();
    const [campaignsGridApi, setCampaignsGridApi] = useState<GridApi>();
    const [keywordsGridApi, setKeywordsGridApi] = useState<GridApi>();
    const [productsGridApi, setProductsGridApi] = useState<GridApi>();
    const [placementsGridApi, setPlacementsGridApi] = useState<GridApi>();
    const [adgroupsGridApi, setAdgroupsGridApi] = useState<GridApi>();
    const [alertsGridApi, setAlertsGridApi] = useState<GridApi>();
    const [goalsGridApi, setTagTypeAggregatedGridApi] = useState<GridApi>();
    const [tagTypeAggregatedGridApi, setGoalsGridApi] = useState<GridApi>();
    const [manualRecommendationsGridApi, setManualRecommendationsGridApi] =
        useState<GridApi>();
    const [autoRecommendationsGridApi, setAutoRecommendationsGridApi] =
        useState<GridApi>();
    const [revinitiRecommendationsGridApi, setRevinitiRecommendationsGridApi] =
        useState<GridApi>();
    const [budgetRecommendationsGridApi, setBudgetRecommendationsGridApi] =
        useState<GridApi>();
    const [
        placementRecommendationsGridApi,
        setPlacementRecommendationsGridApi,
    ] = useState<GridApi>();
    const [DateRange, setDateRange] = useState<IDRange>();
    const [targetSelectedNode, setTargetSelectedNode] = useState<RowNode[]>();
    const [adgroupLoading, setAdgroupLoading] = useState<boolean>(true);
    const [campaignsLoading, setCampaignsLoading] = useState<boolean>(true);
    const [keywordsLoading, setKeywordsLoading] = useState<boolean>(true);
    const [portfoliosLoading, setPortfoliosLoading] = useState<boolean>(true);
    const [productsLoading, setProductsLoading] = useState<boolean>(true);
    const [placementsLoading, setPlacementsLoading] = useState<boolean>(true);
    const [alertsLoading, setAlertsLoading] = useState<boolean>(true);
    const [goalsGridLoading, setGoalsGridLoading] = useState<boolean>(true);
    const [tagTypeAggregatedGridLoading, setTagTypeAggregatedGridLoading] = useState<boolean>(true);
    const [activeRulesLoading, setActiveRulesLoading] = useState<boolean>(true);
    const [manualRecommendationsLoading, setManualRecommendationsLoading] =
        useState<boolean>(true);
    const [autoRecommendationsLoading, setAutoRecommendationsLoading] =
        useState<boolean>(true);
    const [revinitiRecommendationsLoading, setRevinitiRecommendationsLoading] =
        useState<boolean>(true);
    const [budgetRecommendationsLoading, setBudgetRecommendationsLoading] =
        useState<boolean>(true);
    const [
        placementRecommendationsLoading,
        setPlacementRecommendationsLoading,
    ] = useState<boolean>(true);
    const [productMappingGridApi, setProductMappingGridApi] = useState<GridApi>();
    const [productMappingGridLoading, setProductMappingGridLoading] = useState<boolean>(true);
    const [keywordMappingGridApi, setKeywordMappingGridApi] = useState<GridApi>();
    const [keywordMappingGridLoading, setKeywordMappingGridLoading] = useState<boolean>(true);
    const [rulesFilterDisp, setRulesFilterDisp] = useState<boolean>(false);
    const [rulesFilterData, setRulesFilterData] = useState<Rule>();
    const [rulesSummaryDisp, setRulesSummaryDisp] = useState<boolean>();
    const [ruleLogsDate, setRuleLogsDate] = useState<IDateLogs>();
    const [requestModalDisp, setRequestModalDisp] = useState<boolean>();
    const [nameValue, setNameValue] = useState<string>();
    const [keywordsAnalyticsGridApi, setKeywordsAnalyticsGridApi] = useState<GridApi>();

    const currentTime = Math.floor(Date.now() / 1000);
    const [sessionExpiredAlert, setSessionExpiredAlert] =
        useState<boolean>(false);
    const activeTab = getActiveModule().activeModule;
    const caCheckedNode = useSelector(
        (state: ICheckedNode) => state.caCheckedNode.value
    );
    const adCheckedNode = useSelector(
        (state: ICheckedNode) => state.adCheckedNode.value
    );
    const poCheckedNode = useSelector(
        (state: ICheckedNode) => state.poCheckedNode.value
    );
    const productMappingSelectionData = useSelector(
        (state: ICheckedNode) =>
            state.productMappingSelectionData.value
    );
    const activeCheckedNode = useMemo(() => {
        let currentCheckedNode;
        switch (activeTab) {
            case SubModule.PORTFOLIO:
                currentCheckedNode = poCheckedNode;
                break;
            case SubModule.CAMPAIGN:
                currentCheckedNode = caCheckedNode;
                break;
            case SubModule.ADGROUP:
                currentCheckedNode = adCheckedNode;
                break;
            case ParentModule.SETTINGS:
                currentCheckedNode = productMappingSelectionData;
                break;
        }
        return currentCheckedNode;
    }, [activeTab, poCheckedNode, caCheckedNode, adCheckedNode, productMappingSelectionData]);
    const [isAssetUpload, setIsAssetUpload] = useState<boolean>(false);
    const [assetResponse, setAssetResponse] = useState<
        IStrapiAssetResponse | IStrapiResponse[] | []
    >([]);
    const [campaignCreationConfigParams, setCampaignCreationConfigParams] =
        useState<ICellRendererParams>();
    const [customImageName, setCustomImageName] = useState<string>();
    const [activePage, setActivePage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [pageLimit, setPageLimit] = useState<number>(25);
    const [selectedImageFilter, setSelectedImageFilter] =
        useState<string>("logo");
    const [dprTimeSlots, setDprTimeSlots] = useState<TDprSlots>(
        DPR_TIME_SLOTS()
    );
    const [selectedValue, setSelectedValue] = useState<string>();
    const [daypartingCardCheckedData, setDaypartingCardCheckedData] =
        useState<IDprCheckedData>({
            monday: {},
            tuesday: {},
            wednesday: {},
            thursday: {},
            friday: {},
            saturday: {},
            sunday: {},
        });
    const [selectedSellersData, setSelectedSellersData] = useState<string[]>(
        []
    );
    const [selectedBrandsData, setSelectedBrandsData] = useState<number[]>([]);

    const dayPartingDisplay = useSelector(
        (state: TDayPartingDisplay) => state.dayPartingDisplay.value
    );
    const isDaypartingEdit = useSelector(
        (state: TIsDaypartingEdit) => state.isDaypartingEdit.value
    );
    const timezoneChangeHandler = (data: SelectOptions) => {
        if (dayPartingDisplay && isDaypartingEdit) {
            setSelectedValue(data.label);
        } else {
            dispatch(updateClientTimezone(data.label));
        }
    };

    const [isCheckboxSelected, setIsCheckboxSelected] = useState(false);

    const [suggestedNegKwsGridApi, setSuggestedNegKwsGridApi] =
        useState<GridApi>();

    const [existingNegKwsGridApi, setExistingNegKwsGridApi] =
        useState<GridApi>();
    const [sidebarToggleItems, setSidebarToggleItems] = useState<Record<string, boolean>>({});

    const [reviewsLoader, setReviewsLoader] = useState<boolean>(false);

    const [fixedOptions, setFixedOptions] = useState<IFixedReviewOptions>(structuredClone(DEFAULT_REVIEW_OPTIONS));
    const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
    const [selectedSubCategory, setSelectedSubCategory] = useState<string[]>([]);
    const [skusData, setSkusData] = useState<ISkusData[]>([]);
    const [selectedSKU, setSelectedSKU] = useState<string>("");
    const [reviewsBrandData, setReviewsBrandData] = useState<any>({});
    const [reviewsSkuData, setReviewsSkuData] = useState({} as IReviewsSkuData);
    const [cerbosPermissions, setCerbosPermissions] = useState<ICerbosData>({ cerbosCallId: "", requestId: "", results: [] });
    const [selectedDataFrequency, setSelectedDataFrequency] = useState<string>(FrequencySlicerOptions.Daily);
    const [productMappingGridParams,setProductMappingGridParams] = useState({});    const [selectedBrand, setSelectedBrand] = useState<string[]>([]);
    const [keywordMappingGridParams,setKeywordMappingGridParams] = useState({});
    const [selectedCatAdditionalFilters, setSelectedCatAdditionalFilters] = useState<ICategoryAdvFilters>({})
    const [directCompetitorFilter, setDirectCompetitorFilter] = useState<boolean>(false)
    const [powerSkusFilters, setPowerSkusFilter] = useState<boolean>(false)

    const resetReviewFilters = () => {
        if (getActiveModule().activeParentModule === ParentModule.REVIEW_ANALYTICS) {
            setQueryParams({});
            setFixedOptions(structuredClone(DEFAULT_REVIEW_OPTIONS));
            setSelectedCategory([]);
            setSelectedSubCategory([]);
            setSkusData([]);
            setSelectedSKU("");
        }
        if (getActiveModule().activeModule === SubModule.CATEGORY_INSIGHTS) {
            setSelectedCategory([]);
            setSelectedSubCategory([]);
            setSelectedBrand([]);
        }
    }

    const contextValues: IMainContext = {
        dates,
        setDates,
        rulesApi,
        setRulesApi,
        alertsData,
        setAlertsData,
        activeRulesData,
        setActiveRulesData,
        portfoliosGridApi,
        setPortfoliosGridApi,
        campaignsGridApi,
        setCampaignsGridApi,
        keywordsGridApi,
        setKeywordsGridApi,
        productsGridApi,
        setProductsGridApi,
        placementsGridApi,
        setPlacementsGridApi,
        adgroupsGridApi,
        setAdgroupsGridApi,
        DateRange,
        setDateRange,
        targetSelectedNode,
        setTargetSelectedNode,
        adgroupLoading,
        setAdgroupLoading,
        campaignsLoading,
        setCampaignsLoading,
        keywordsLoading,
        setKeywordsLoading,
        portfoliosLoading,
        setPortfoliosLoading,
        productsLoading,
        setProductsLoading,
        placementsLoading,
        setPlacementsLoading,
        keywordsAnalyticsGridApi,
        setKeywordsAnalyticsGridApi,
        alertsLoading,
        setAlertsLoading,
        goalsGridLoading,
        setGoalsGridLoading,
        goalsGridApi,
        setGoalsGridApi,
        tagTypeAggregatedGridApi,
        setTagTypeAggregatedGridApi,
        tagTypeAggregatedGridLoading,
        setTagTypeAggregatedGridLoading,
        activeRulesLoading,
        setActiveRulesLoading,
        alertsGridApi,
        setAlertsGridApi,
        rulesFilterDisp,
        setRulesFilterDisp,
        rulesSummaryDisp,
        setRulesSummaryDisp,
        rulesFilterData,
        setRulesFilterData,
        ruleLogsDate,
        setRuleLogsDate,
        requestModalDisp,
        setRequestModalDisp,
        nameValue,
        setNameValue,
        currentTime,
        sessionExpiredAlert,
        setSessionExpiredAlert,
        activeCheckedNode,
        isAssetUpload,
        setIsAssetUpload,
        assetResponse,
        setAssetResponse,
        campaignCreationConfigParams,
        setCampaignCreationConfigParams,
        customImageName,
        setCustomImageName,
        activePage,
        setActivePage,
        totalPages,
        setTotalPages,
        pageLimit,
        setPageLimit,
        selectedImageFilter,
        setSelectedImageFilter,
        dprTimeSlots,
        setDprTimeSlots,
        selectedValue,
        setSelectedValue,
        daypartingCardCheckedData,
        setDaypartingCardCheckedData,
        selectedSellersData,
        setSelectedSellersData,
        selectedBrandsData,
        setSelectedBrandsData,
        timezoneChangeHandler,
        manualRecommendationsGridApi,
        setManualRecommendationsGridApi,
        autoRecommendationsGridApi,
        setAutoRecommendationsGridApi,
        revinitiRecommendationsGridApi,
        setRevinitiRecommendationsGridApi,
        budgetRecommendationsGridApi,
        setBudgetRecommendationsGridApi,
        placementRecommendationsGridApi,
        setPlacementRecommendationsGridApi,
        manualRecommendationsLoading,
        setManualRecommendationsLoading,
        autoRecommendationsLoading,
        setAutoRecommendationsLoading,
        revinitiRecommendationsLoading,
        setRevinitiRecommendationsLoading,
        budgetRecommendationsLoading,
        setBudgetRecommendationsLoading,
        placementRecommendationsLoading,
        setPlacementRecommendationsLoading,
        isCheckboxSelected,
        setIsCheckboxSelected,
        suggestedNegKwsGridApi,
        setSuggestedNegKwsGridApi,
        existingNegKwsGridApi,
        setExistingNegKwsGridApi,
        productMappingGridLoading,
        setProductMappingGridLoading,
        productMappingGridApi,
        setProductMappingGridApi,
        reviewsLoader,
        setReviewsLoader,
        fixedOptions,
        setFixedOptions,
        selectedCategory,
        setSelectedCategory,
        selectedSubCategory,
        setSelectedSubCategory,
        skusData,
        setSkusData,
        selectedSKU,
        setSelectedSKU,
        reviewsBrandData,
        setReviewsBrandData,
        reviewsSkuData,
        setReviewsSkuData,
        resetReviewFilters,
        cerbosPermissions,
        setCerbosPermissions,
        sidebarToggleItems,
        setSidebarToggleItems,
        selectedDataFrequency,
        setSelectedDataFrequency,
        productMappingGridParams,
        setProductMappingGridParams,
        selectedBrand,
        setSelectedBrand,
        selectedCatAdditionalFilters,
        setSelectedCatAdditionalFilters,
        directCompetitorFilter,
        setDirectCompetitorFilter,
        powerSkusFilters,
        setPowerSkusFilter,
        keywordMappingGridLoading,
        setKeywordMappingGridLoading,
        keywordMappingGridApi,
        setKeywordMappingGridApi,
        keywordMappingGridParams,
        setKeywordMappingGridParams,
    };

    return (
        <>
            <mainContext.Provider value={contextValues}>
                {props.children}
            </mainContext.Provider>
        </>
    );
};

export default ContextWrapper;
